/**
 * useAlertasRealtime — subscreve em mudancas na tab_alertas_operacionais
 * para o tenant atual via Supabase Realtime.
 *
 * Comportamento (espelha o useNotificacoesRealtime do web-client):
 *   1. Inválida queries 'alertas-abertos' e 'alertas-list' (badge e lista
 *      atualizam no ato).
 *   2. Em INSERT, dispara um toast leve com o titulo (a menos que silenciado).
 *
 * Hardening: silenciarToast vai pra um ref para não re-subscrever o canal
 * (e perder mensagens em transit) toda vez que muda de rota.
 *
 * RLS de tab_alertas_operacionais já restringe por seq_tenant; aqui filtramos
 * apenas por cod_status='ABERTO' para diminuir o trafego (so notifica novos
 * alertas em aberto, não alertas que mudam de status para resolvido etc).
 *
 * Tolerante a falhas: se Realtime não estiver habilitado para a tabela ou a
 * connection cair, não quebra a UI.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';

interface AlertaPayload {
  seq_alerta_operacional: number;
  cod_severidade: 'INFO' | 'AVISO' | 'ALTA' | 'CRITICA';
  cod_status: 'ABERTO' | 'RECONHECIDO' | 'RESOLVIDO' | 'IGNORADO';
  des_titulo: string;
  des_mensagem: string | null;
}

interface UseAlertasRealtimeOptions {
  /** Se true, não mostra toast em INSERT (para usar dentro da própria tela). */
  silenciarToast?: boolean;
}

const TOAST_VARIANTE: Record<
  AlertaPayload['cod_severidade'],
  (msg: string, opts?: object) => void
> = {
  INFO: (m, o) => toast.info(m, o),
  AVISO: (m, o) => toast.warning(m, o),
  ALTA: (m, o) => toast.error(m, o),
  CRITICA: (m, o) => toast.error(m, o),
};

export function useAlertasRealtime(options: UseAlertasRealtimeOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // ref evita re-subscrever o canal quando silenciarToast muda (ex: navegacao)
  const silenciarRef = useRef(options.silenciarToast ?? false);
  silenciarRef.current = options.silenciarToast ?? false;

  useEffect(() => {
    if (!user?.id) return;

    const canal = supabase
      .channel(`alertas:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tab_alertas_operacionais',
        },
        (payload) => {
          // Inválida ambas queries — badge de "abertos" + listas
          void queryClient.invalidateQueries({ queryKey: ['alertas-abertos'] });
          void queryClient.invalidateQueries({ queryKey: ['alertas-list'] });

          // Toast apenas em INSERT de alerta ABERTO e se não silenciado
          if (payload.eventType === 'INSERT' && !silenciarRef.current) {
            const novo = payload.new as AlertaPayload;
            if (novo.cod_status === 'ABERTO') {
              const showToast = TOAST_VARIANTE[novo.cod_severidade] ?? TOAST_VARIANTE.INFO;
              showToast(novo.des_titulo, {
                description: novo.des_mensagem ?? undefined,
              });
            }
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(canal);
    };
  }, [user?.id, queryClient]);
}
